import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

export type PlanStatus =
  | "loading"
  | "thinking"
  | "asking_question"
  | "plan_ready"
  | "approved"
  | "skipped"
  | "error";

export interface ClarifyingQuestion {
  text: string;
  options: Array<{ label: string; description: string }>;
  allow_other: boolean;
}

export interface Plan {
  overview: string;
  features: string[];
  screens: string[];
  design_direction: string;
}

interface BuildPlanRow {
  id: string;
  project_id: string;
  user_id: string;
  prompt: string;
  plan_text: Plan | null;
  clarifying_questions: ClarifyingQuestion[];
  plan_status: string;
}

interface UsePlanModeReturn {
  status: PlanStatus;
  currentQuestion: ClarifyingQuestion | null;
  plan: Plan | null;
  error: string | null;
  prompt: string;
  questionsAsked: number;
  buildPlanId: string | null;
  submitAnswer: (answer: string) => Promise<void>;
  approvePlan: () => Promise<void>;
  skipPlan: () => Promise<void>;
}

export function usePlanMode(projectId: string | undefined): UsePlanModeReturn {
  const [status, setStatus] = useState<PlanStatus>("loading");
  const [currentQuestion, setCurrentQuestion] = useState<ClarifyingQuestion | null>(null);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [prompt, setPrompt] = useState("");
  const [questionsAsked, setQuestionsAsked] = useState(0);
  const [buildPlanId, setBuildPlanId] = useState<string | null>(null);

  const navigate = useNavigate();
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const handleSSEEvent = useCallback((event: Record<string, unknown>) => {
    if (event.type === "error") {
      setError((event.message || "Unknown error") as string);
      setStatus("error");
      return;
    }

    if (event.type === "state") {
      const planStatus = event.plan_status as string | undefined;
      const bpId = event.build_plan_id as string | undefined;
      if (bpId) setBuildPlanId(bpId);
      if (planStatus === "thinking") setStatus("thinking");
      return;
    }

    if (event.type === "complete") {
      const payload = event.payload as {
        build_plan_id?: string;
        action?: string;
        question?: ClarifyingQuestion;
        plan?: Plan;
        questions_asked?: number;
      } | undefined;

      if (!payload) return;

      if (payload.build_plan_id) setBuildPlanId(payload.build_plan_id);
      if (typeof payload.questions_asked === "number") {
        setQuestionsAsked(payload.questions_asked);
      }

      if (payload.action === "ask_question" && payload.question) {
        setCurrentQuestion(payload.question);
        setStatus("asking_question");
      } else if (payload.action === "plan_ready" && payload.plan) {
        setPlan(payload.plan);
        setCurrentQuestion(null);
        setStatus("plan_ready");
      }
      return;
    }
  }, []);

  const callPlanModeSSE = useCallback(async (
    mode: "start" | "answer",
    planId: string,
    promptOrAnswer: string
  ) => {
    setStatus("thinking");
    setError(null);

    // Abort any existing request
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError("Not signed in");
        setStatus("error");
        return;
      }

      const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL || "").replace(/\/$/, "");
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const body = mode === "start"
        ? { mode: "start", project_id: projectId, prompt: promptOrAnswer }
        : { mode: "answer", build_plan_id: planId, answer: promptOrAnswer };

      const response = await fetch(`${supabaseUrl}/functions/v1/plan-mode`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: anonKey,
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(`Plan mode failed: ${response.status} ${text.slice(0, 200)}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error("No response body");

      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const jsonStr = line.slice(6).trim();
          if (!jsonStr) continue;

          try {
            const event = JSON.parse(jsonStr);
            handleSSEEvent(event);
          } catch {
            console.warn("[usePlanMode] Skipping malformed SSE JSON");
          }
        }
      }
    } catch (err) {
      if ((err as Error).name === "AbortError") return;
      setError(err instanceof Error ? err.message : "Request failed");
      setStatus("error");
    }
  }, [projectId, handleSSEEvent]);

  // Load existing build_plan or start new plan
  useEffect(() => {
    if (!projectId) return;

    const loadPlan = async () => {
      setStatus("loading");
      setError(null);

      try {
        // Query build_plans by project_id (RLS enforces ownership)
        const { data: row, error: fetchError } = await supabase
          .from("build_plans")
          .select("*")
          .eq("project_id", projectId)
          .maybeSingle();

        if (fetchError) {
          setError(fetchError.message);
          setStatus("error");
          return;
        }

        if (!row) {
          // No plan exists for this project — shouldn't happen if routed correctly
          setError("No plan found for this project");
          setStatus("error");
          return;
        }

        const buildPlan = row as BuildPlanRow;
        setBuildPlanId(buildPlan.id);
        setPrompt(buildPlan.prompt);

        // Hydrate UI based on current status
        if (buildPlan.plan_status === "approved") {
          setPlan(buildPlan.plan_text);
          setStatus("approved");
        } else if (buildPlan.plan_status === "skipped") {
          setStatus("skipped");
        } else if (buildPlan.plan_status === "plan_ready" && buildPlan.plan_text) {
          setPlan(buildPlan.plan_text);
          setStatus("plan_ready");
        } else if (buildPlan.plan_status === "asking_questions" && buildPlan.clarifying_questions?.length > 0) {
          const questions = buildPlan.clarifying_questions;
          setQuestionsAsked(questions.length);
          setCurrentQuestion(questions[questions.length - 1]);
          setStatus("asking_question");
        } else if (buildPlan.plan_status === "draft") {
          // Start the plan generation via SSE
          await callPlanModeSSE("start", buildPlan.id, buildPlan.prompt);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load plan");
        setStatus("error");
      }
    };

    loadPlan();
  }, [projectId, callPlanModeSSE]);

  const submitAnswer = useCallback(async (answer: string) => {
    if (!buildPlanId) {
      setError("No plan ID");
      return;
    }
    await callPlanModeSSE("answer", buildPlanId, answer);
  }, [buildPlanId, callPlanModeSSE]);

  const approvePlan = useCallback(async () => {
    if (!buildPlanId || !projectId) {
      setError("No plan ID");
      return;
    }

    try {
      const { error: updateError } = await supabase
        .from("build_plans")
        .update({ plan_status: "approved", approved_at: new Date().toISOString() })
        .eq("id", buildPlanId);

      if (updateError) {
        setError(updateError.message);
        return;
      }

      setStatus("approved");

      // Build enriched prompt from plan
      const enrichedPrompt = plan
        ? `${prompt}\n\n## Plan\n${plan.overview}\n\n### Features\n${plan.features.map(f => `- ${f}`).join("\n")}\n\n### Screens\n${plan.screens.map(s => `- ${s}`).join("\n")}\n\n### Design Direction\n${plan.design_direction}`
        : prompt;

      // Navigate to code-workspace with autoBuild
      navigate(`/code-workspace?projectId=${projectId}`, {
        state: {
          autoBuild: true,
          prefill: enrichedPrompt,
          source: "plan_mode_approved",
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to approve plan");
    }
  }, [buildPlanId, projectId, plan, prompt, navigate]);

  const skipPlan = useCallback(async () => {
    if (!buildPlanId || !projectId) {
      setError("No plan ID");
      return;
    }

    try {
      const { error: updateError } = await supabase
        .from("build_plans")
        .update({ plan_status: "skipped" })
        .eq("id", buildPlanId);

      if (updateError) {
        setError(updateError.message);
        return;
      }

      setStatus("skipped");

      // Navigate to code-workspace with autoBuild using original prompt
      navigate(`/code-workspace?projectId=${projectId}`, {
        state: {
          autoBuild: true,
          prefill: prompt,
          source: "plan_mode_skipped",
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to skip plan");
    }
  }, [buildPlanId, projectId, prompt, navigate]);

  return {
    status,
    currentQuestion,
    plan,
    error,
    prompt,
    questionsAsked,
    buildPlanId,
    submitAnswer,
    approvePlan,
    skipPlan,
  };
}
